import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [devToken, setDevToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.dev_token) {
        setDevToken(data.dev_token);
        setStep(2);
        toast.success("Reset token generated (dev mode)");
      } else {
        toast.success("If the email exists, a reset link was sent");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: devToken, password: newPassword });
      toast.success("Password updated. Sign in now.");
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 card-soft border border-slate-200/60">
        <div className="mb-6 text-center">
          <Link to="/" className="inline-flex items-center gap-2.5 group cursor-pointer" data-testid="forgot-logo-link">
            <div className="w-9 h-9 rounded-2xl bg-slate-900 grid place-items-center overflow-hidden group-hover:scale-105 transition-transform">
              <Wallet className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="heading text-xl font-bold text-slate-900 tracking-tight group-hover:text-slate-800 transition-colors">GEMS</span>
          </Link>
        </div>
        <h1 className="heading text-2xl font-bold text-slate-900">Reset password</h1>
        <p className="text-sm text-slate-500 mt-1">
          {step === 1 && "Enter your email to receive a reset token."}
          {step === 2 && "Set a new password. In dev, the token is auto-filled below."}
          {step === 3 && "Password updated. You can sign in now."}
        </p>

        {step === 1 && (
          <form onSubmit={request} className="space-y-4 mt-6" data-testid="forgot-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-2xl"
                required
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              disabled={busy}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="forgot-submit-button"
            >
              {busy ? "Sending…" : "Send reset token"}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={reset} className="space-y-4 mt-6">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Token</Label>
              <Input value={devToken} onChange={(e) => setDevToken(e.target.value)} className="mt-2 h-12 rounded-2xl font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">New password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={6}
                required
                className="mt-2 h-12 rounded-2xl"
                data-testid="forgot-newpw-input"
              />
            </div>
            <Button
              disabled={busy}
              className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="forgot-reset-button"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}

        {step === 3 && (
          <div className="mt-6">
            <Link to="/login" className="inline-block px-4 py-2 rounded-full bg-emerald-600 text-white font-semibold">
              Go to sign in
            </Link>
          </div>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-2">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
